import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import { fontSize, spacing, fontWeight } from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import { gray02 } from 'presentations/views/styles/color';
import Avatar, { Group } from 'presentations/views/components/atoms/Avatar';
import Dialog from 'presentations/views/components/atoms/Dialog';
import { UserType } from 'domains/models/event_type_user/user_type';
import { useState } from 'react';

interface EventUserGroupListItem {
  uid: string;
  name: string;
  profileImage: string | null;
  userType: UserType;
  isAnonymous?: boolean;
  email?: string | null;
}

interface Props {
  users: EventUserGroupListItem[];
  title?: string;
  groupMaxCount?: number;
  avatarWidth?: number;
  avatarHeight?: number;
  disableUserList?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [isOpenUserList, setIsOpenUserList] = useState<boolean>(false);

  const onClickUserList = async () => {
    setIsOpenUserList(true);
  };
  const onCloseUserList = async () => {
    setIsOpenUserList(false);
  };
  const groupMaxCount = props.groupMaxCount ?? 6;

  return (
    <>
      <UserList
        onClick={props.disableUserList ? undefined : onClickUserList}
        disableUserList={props.disableUserList}
      >
        <UserGroup
          max={groupMaxCount}
          width={props.avatarWidth}
          height={props.avatarHeight}
        >
          {props.users.map((user) => (
            <UserAvatar
              key={user.uid}
              src={user.profileImage}
              alt={user.name}
              width={props.avatarWidth}
              height={props.avatarHeight}
            >
              {user.name.charAt(0)}
            </UserAvatar>
          ))}
        </UserGroup>
        {!props.disableUserList && <MoreIcon name="expand_more" />}
      </UserList>
      <Dialog
        isOpen={isOpenUserList}
        onClose={onCloseUserList}
        title={props.title || t('title')}
      >
        <ListContainer>
          {props.users.map((eventUser) => (
            <ListItem key={eventUser.uid} role="button">
              <AvatarContainer>
                <Avatar src={eventUser.profileImage} alt={eventUser.name} />
              </AvatarContainer>
              <NameContainer>
                <Name>{eventUser.name || eventUser.uid}</Name>
                {!eventUser.isAnonymous && eventUser.email && (
                  <EventUserMail>{eventUser.email}</EventUserMail>
                )}
              </NameContainer>
              {eventUser.userType === UserType.Host && (
                <EventUserType>{t('Host')}</EventUserType>
              )}
            </ListItem>
          ))}
        </ListContainer>
      </Dialog>
    </>
  );
}

const UserAvatar = styled(Avatar)<{ width?: number; height?: number }>`
  width: ${(props) => `${props.width ?? 24}px`};
  height: ${(props) => `${props.height ?? 24}px`};
  font-weight: ${fontWeight('bold')};
`;
const UserList = styled.div<{ disableUserList?: boolean }>`
  margin-right: ${spacing(8)};
  display: flex;
  align-items: center;
  cursor: ${(props) => `${props.disableUserList ? 'initial' : 'pointer'}`};
`;
const MoreIcon = styled(Icon)`
  color: ${gray02};
  width: 18px !important;
  height: 18px !important;
`;
const UserGroup = styled(Group)<{ width?: number; height?: number }>`
  .MuiAvatarGroup-avatar {
    width: ${(props) => `${props.width ?? 24}px`};
    height: ${(props) => `${props.height ?? 24}px`};
    font-size: ${fontSize(10)};
    font-weight: ${fontWeight('bold')};
  }
`;
const ListContainer = styled.div``;

const ListItem = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const AvatarContainer = styled.div`
  margin-right: ${spacing(16)};
`;
const NameContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex: 1;
`;
const Name = styled.div`
  font-weight: ${fontWeight('bold')};
`;
const EventUserMail = styled.div`
  font-size: ${fontSize(11)};
  color: ${gray02};
`;
const EventUserType = styled.div`
  font-size: ${fontSize(11)};
  color: ${gray02};
  width: 48px;
  margin-left: ${spacing(16)};
`;
